import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import "./VehicleCompatibilityInfoModal.scss"
import { useMediaQuery } from 'react-responsive';
import { Button, Row, Col, Drawer, Table, Alert, Space, Typography } from 'antd';
import { useCurrentCompany } from '../../features/company/companySlice.js';
import { isValidHttpUrl, toTitleCase } from '../../utils/strings';
import { fetchDocument, useGetDocument } from '../../features/document/documentApi';
import { useDispatch } from 'react-redux';
import { useUserKey } from '../../features/user/ssoSlice';
import { Can, useCan } from '../../features/user/Can';
import {
  CloudDownloadOutlined,
  InfoCircleFilled,
} from '@ant-design/icons';
import { getDisplaySource } from './VehicleModelHelper';
import style from './VehicleCompatibility.module.scss';
import VehicleCompatibilityInfoModal from './VehicleCompatibilityInfoModal.js';
import { PanelBody, Panel } from '../../components/panel/panel.js';

const { Title, Text } = Typography;

const VehicleCompatibilityDetailModal = ({ vehicleDetail, isModalOpen, onClose }) => {
  const { i18n, t } = useTranslation();
  const dispatch = useDispatch();
  const userKey = useUserKey();
  const can = useCan();
  const currentCompany = useCurrentCompany();
  const isMobile = useMediaQuery({ maxWidth: 767 });
  const [infoModelOpen, setInfoModalOpen] = useState(false);

  const { data: documents, isFetching: isFetchingDocument } = useGetDocument(currentCompany.id);
  const documentMap = useMemo(() => {
    return Object.fromEntries((documents || []).filter(i => (i.data || []).length > 0).map(item => [item.attachmentId, item]));
  }, [documents, isFetchingDocument]);

  const tableColumn = [
    {
      title: i18n.t('VehicleModel.SupportedDevices'),
      width: '30%',
      render: (e, r) => <div>{r.deviceModel}</div>
    },
    {
      title: i18n.t('VehicleModel.SupportedCables'),
      width: '70%',
      render: (e, r) => {
        if ((e.sapCode || []).length === 0) {
          return "N/A";
        }
        else {
          return <Space direction="vertical">{r.sapCode.map(i => i.sapCode )}</Space>
        }
      }
    }
  ]

  return (
    <Drawer
      title={`${vehicleDetail?.make} ${vehicleDetail?.model} ${t("VehicleModel.CompatibilitySheet")}`}
      placement={isMobile ? "bottom" : "right"}
      height={"90%"}
      width={"30%"}
      onClose={onClose}
      open={isModalOpen}
    >
      <>
        {
          (vehicleDetail?.groupModel || []).sort((a, b) => (a.priority || 1) - (b.priority || 1)).map(model => {
            const isCanCliqRequired = (model.extras?.connectionPoint || []).includes("CANClick");

            return (
              <Panel style={{ marginBottom: "15px" }} bodyStyle={{ background: "#f7f7f7", padding: 12 }}>
                <PanelBody>
                  <Space direction='vertical' style={{width:"100%"}}>
                    {model.source === "inventure" && <Row>
                      <Col xs={{ span: 24 }} style={{ marginTop: 16, marginBottom: 16 }}>
                        {
                          model.supportedModels.find(i => i.name === "SI201") === undefined ? <Alert
                            message={t('VehicleModel.CustomNote.InstallationRecommended')}
                            type="info"
                          /> :
                            <Alert
                              message={t('VehicleModel.CustomNote.PlugAndPlaySupported')}
                              type="info"
                            />
                        }
                      </Col>
                    </Row>}
                    <Row>
                      <Col xs={{ span: 24 }}>
                        <Title level={4} className={style.panelHeader}>{model.displaySource}</Title>
                      </Col>
                    </Row>
                    {
                      (model.supportedModels || []).length !== 0 &&
                      <Row>
                        <Col xs={{ span: 24 }}>
                          <Table columns={tableColumn} dataSource={model.supportedModels.map(i => {
                            return {
                              deviceModel: i.name,
                              sapCode: (model.supportedKit || []).filter(k => k.deviceModelId === i.id)
                            }
                          })} pagination={false} size="small" />
                        </Col>
                      </Row>
                    }
                    {isCanCliqRequired &&
                      <Row>
                        <Col xs={{ span: 24 }}>
                          <Row>
                            <Col xs={{ span: 24 }}>
                              <Text type="secondary" className={style.subHeader}>{t("VehicleModel.ConnectionPoint")}</Text>
                            </Col>
                            <Col xs={{ span: 24 }} style={{padding:5}}>
                              <Text>{t("VehicleModel.CustomNote.CanCliqRequired")}</Text>
                            </Col>
                          </Row>
                        </Col>
                      </Row>
                    }
                    <Row>
                      <Col xs={{ span: 24 }} style={{ display: "flex", flexDirection: "column" }}>
                        <Text type="secondary" className={style.subHeader}>{t("VehicleModel.Documentation")}</Text>
                        <Space size={[16, 0]} wrap>
                          {(model.attachments || []).length > 0 ? model.attachments.map((i, index) => {
                            const doc = documentMap[i.attachmentId];
                            return <Button key={index} style={{ padding: 0 }} onClick={() => {
                              var win = window.open();
                              if (doc.mimeType === "text/*") {
                                win.location = doc.url;
                              }
                              else {
                                fetchDocument(i.documentId, dispatch, userKey).then(e => {
                                  if (isValidHttpUrl(e)) {
                                    win.location = e;
                                  }
                                  else {
                                    win.document.write(e);
                                  }
                                }).catch(e => win.close());
                              }
                            }} type="link" className={style.infoBtnIcon}
                              icon={<InfoCircleFilled onClick={(e) => {
                                setInfoModalOpen(true);
                                e.stopPropagation();
                              }} className={style.infoIcon} />}>
                              {doc?.name}
                            </Button>;
                          }) : <Text> - </Text>}
                        </Space>
                      </Col>
                    </Row>
                  </Space>
                </PanelBody>
              </Panel>
            )
          })
        }
        <VehicleCompatibilityInfoModal
          isModalOpen={infoModelOpen}
          onClose={() => { setInfoModalOpen(false) }} />
      </>

    </Drawer>
  )
}

export default VehicleCompatibilityDetailModal;